import { computed, nanoid, queryDb } from "@livestore/livestore";
import type { Store } from "@livestore/livestore";

import { DisposePool, dev, memoFn } from "@phosphor/utils";
import { emitDebugValue, emitDebugValueFn } from "#scripts/lib/dev/emitDebugValue.ts";
import type { LiveQuery } from "./LiveQuery.ts";
import { uiState$ } from "./livestore/queries.js";
import { events, tables } from "./livestore/schema.js";

// #region todo-vm-livestore
export type TodoItemVM = {
  key: string;
  text$: LiveQuery<string>; // !todo.text
  completed$: LiveQuery<boolean>; // !todo.completed
  toggleCompleted: () => void;
  remove: () => void;
};

export type TodoListVM = {
  header: {
    newTodoText$: LiveQuery<string>; // !newTodoText
    updateNewTodoText: (text: string) => void;
    addTodo: () => void;
  };
  itemList: {
    items$: LiveQuery<TodoItemVM[]>; // !visibleTodos
  };
  footer: {
    incompleteCount$: LiveQuery<number>; // !incompleteCount
    currentFilter$: LiveQuery<"all" | "active" | "completed">; // !currentFilter
    showAll: () => void;
    showActive: () => void;
    showCompleted: () => void;
    clearCompleted: () => void;
  };
};
// #endregion todo-vm-livestore

// #region todo-scope-livestore
const createID = (name: string) => `${name}_${nanoid(12)}`; // #muted
export function createTodoListScope(store: Store): TodoListVM {
  const currentFilter$ = computed(
    (query) => query(uiState$).filter,
    { label: "filter" }, // #muted
  );
  const newTodoText$ = computed(
    (query) => query(uiState$).newTodoText,
    { label: "newTodoText" }, // #muted
  );
  const createTodoItemVM = memoFn((id: string): TodoItemVM => {
    const completed$ = queryDb(
      () =>
        tables.todos.select("completed").where({ id }).first({
          behaviour: "error",
        }),
      { label: "todoItem.completed", deps: [id] }, // #muted
    );
    return {
      key: id,
      completed$,
      text$: queryDb(
        () =>
          tables.todos.select("text").where({ id }).first({
            behaviour: "error",
          }),
        { label: "todoItem.text", deps: [id] }, // #muted
      ),
      toggleCompleted: () =>
        store.commit(store.query(completed$) ? events.todoUncompleted({ id }) : events.todoCompleted({ id })),
      remove: () => store.commit(events.todoDeleted({ id, deletedAt: new Date() })),
    };
  });
  const visibleTodosQuery = (filter: "all" | "active" | "completed") =>
    queryDb(
      () =>
        tables.todos.where({
          completed: filter === "all" ? undefined : filter === "completed",
          deletedAt: { op: "=", value: null },
        }),
      {
        label: "visibleTodos", // #muted
        map: (rows) => rows.map((row) => createTodoItemVM(row.id)),
        deps: [filter],
      },
    );
  const visibleTodos$ = computed(
    (query) => {
      const filter = query(currentFilter$);
      return query(visibleTodosQuery(filter));
    },
    { label: "visibleTodos" }, // #muted
  );

  const incompleteCount$ = queryDb(
    tables.todos.count().where({ completed: false, deletedAt: null }),
    { label: "incompleteCount" }, // #muted
  );

  // #hide debugs
  store.subscribe(newTodoText$, emitDebugValueFn("!newTodoText", dev``.ctx({ loc: "phosphor-blog/content/_sources/view-model-pattern/todo-vm/scope.ts:100:66" })));
  store.subscribe(currentFilter$, emitDebugValueFn("!currentFilter", dev``.ctx({ loc: "phosphor-blog/content/_sources/view-model-pattern/todo-vm/scope.ts:101:70" })));
  store.subscribe(incompleteCount$, emitDebugValueFn("!incompleteCount", dev``.ctx({ loc: "phosphor-blog/content/_sources/view-model-pattern/todo-vm/scope.ts:102:74" })));
  let pool = new DisposePool();
  store.subscribe(visibleTodos$, (todos) => {
    pool.dispose();
    pool = new DisposePool();
    const [first] = todos;
    if (first) {
      pool.add(store.subscribe(first.completed$, emitDebugValueFn("!todo.completed", dev``.ctx({ loc: "phosphor-blog/content/_sources/view-model-pattern/todo-vm/scope.ts:109:86" }))));
      pool.add(store.subscribe(first.text$, emitDebugValueFn("!todo.text", dev``.ctx({ loc: "phosphor-blog/content/_sources/view-model-pattern/todo-vm/scope.ts:110:76" }))));
    } else {
      emitDebugValue("!todo.text", dev``.ctx({ loc: "phosphor-blog/content/_sources/view-model-pattern/todo-vm/scope.ts:112:36" }), null);
      emitDebugValue("!todo.completed", dev``.ctx({ loc: "phosphor-blog/content/_sources/view-model-pattern/todo-vm/scope.ts:113:41" }), null);
    }
  });
  // #endhide debugs

  return {
    header: {
      newTodoText$,
      updateNewTodoText: (text: string) => store.commit(events.uiStateSet({ newTodoText: text })),
      addTodo: () => {
        const newTodoText = store.query(newTodoText$).trim();
        if (newTodoText) {
          store.commit(
            events.todoCreated({ id: createID("todo"), text: newTodoText }),
            events.uiStateSet({ newTodoText: "" }), // update text
          );
        }
      },
    },
    itemList: {
      items$: visibleTodos$,
    },
    footer: {
      incompleteCount$,
      currentFilter$,
      showAll: () => store.commit(events.uiStateSet({ filter: "all" })),
      showActive: () => store.commit(events.uiStateSet({ filter: "active" })),
      showCompleted: () => store.commit(events.uiStateSet({ filter: "completed" })),
      clearCompleted: () => store.commit(events.todoClearedCompleted({ deletedAt: new Date() })),
    },
  };
}
// #endregion todo-scope-livestore
